import { useAppConfigStore } from '@pequity/shared/stores/appConfig';
import { ACCESS_TOKEN, REFRESH_TOKEN } from '@pequity/shared/types/constants';
import type { InvitationLoginPayload, LoginPayload, UserPatchPayload, UserProfile } from '@pequity/shared/types/user';
import { getCorrelationId, removeCorrelationId, setCorrelationId } from '@pequity/shared/utils/correlation';
import { isOidcSession } from '@pequity/shared/utils/oidc';
import { setAccessToken } from '@pequity/shared/utils/token';
import type { AxiosError } from 'axios';
import { defineStore } from 'pinia';
import type { RouteLocationNormalized } from 'vue-router';
import {
  deleteProfileImage,
  deleteSelf,
  getUserProfile,
  invitationLogin,
  login,
  logoutRequest,
  patchUser,
  publicLogin,
  publicMfaVerification,
  refreshToken,
  resendMfaToken,
  updateProfileImage,
  verifyCredentials,
  verifyMfaToken,
} from '@/api/auth';
import { cancelOidcRefresh } from '@/composables/useOidcTokenRefresh';
import { stopSmCookieRefresh } from '@/composables/useSmCookieRefresh';
import { loadLocaleMessages, setLocaleInLocalStorage } from '@/plugins/i18n';
import { clearTokens, setRefreshToken, submitAimLogoutForm } from '@/utils/helpers';

export const useAuthStore = defineStore('auth', {
  state: () => ({
    loggedIn: false,
    path: '',
    profile: {} as UserProfile,
  }),
  getters: {
    userId: (state) => state.profile.id,
    isUserAdmin: (state) => !!state.profile.is_admin,
    isUserTeamlead: (state) => !!state.profile.is_teamlead,
    hasPeopleAccess: (state) => !!state.profile.has_people_access,
    loginRedirectPath: (state) => (currentRoute: RouteLocationNormalized) => {
      return state.path || currentRoute.query?.redirect || '/';
    },
    appAccess: (state) => state.profile.app_access,
    isUserStaff: (state) => !!state.profile.is_staff,
    isStaffMode: (state) => state.loggedIn && !!state.profile.staff_mode,
  },
  actions: {
    // Profile actions
    async setUserProfileAndLoggedInState() {
      const { data, error } = await getUserProfile();

      if (!error) {
        this.profile = data;
        this.loggedIn = true;
      }
    },
    async updateProfileImage(image: File) {
      const { error } = await updateProfileImage(this.profile.id, image);
      if (!error) {
        await this.setUserProfileAndLoggedInState();
      }
    },
    async deleteProfileImage() {
      const { error } = await deleteProfileImage(this.profile.id);
      if (!error) {
        await this.setUserProfileAndLoggedInState();
      }
    },
    async changeProfileInfo(data: UserPatchPayload) {
      const { error, response } = await patchUser(this.profile.id, data);
      if (!error) {
        if (data.locale) {
          setLocaleInLocalStorage(data.locale);
          loadLocaleMessages(data.locale);
        }

        this.profile = { ...this.profile, ...response.data };
      }
    },

    // Authentication actions (standalone only)
    async postLogin(payload: LoginPayload) {
      const appConfigStore = useAppConfigStore();
      const { data, error, response } = appConfigStore.showTenantFinder
        ? await login(payload)
        : await publicLogin(payload);

      if (!error && data.access && data.refresh) {
        setAccessToken(data.access);
        setRefreshToken(data.refresh);
      }

      return { response, error };
    },
    async publicLoginWithTenant(payload: LoginPayload & { schema_name?: string; session_token?: string }) {
      const { data, error, response } = await publicLogin(payload);

      if (!error) {
        if (data.mfa_required) {
          setCorrelationId(response.headers['x-correlation-id']);
          return { response, error, mfaRequired: true };
        }
        if (data.access && data.refresh) {
          setAccessToken(data.access);
          setRefreshToken(data.refresh);
        }
      }

      return { response, error, mfaRequired: false };
    },
    async publicVerifyMfaToken(payload: { token: string; schema_name?: string; session_token?: string }) {
      try {
        const { data } = await publicMfaVerification(payload, getCorrelationId());

        removeCorrelationId();
        setAccessToken(data.access_token);
        setRefreshToken(data.refresh_token);

        return { data, error: null };
      } catch (e) {
        const error = e as AxiosError;
        return { data: null, error };
      }
    },
    async credentialsLoginStep(payload: LoginPayload) {
      const { response, error } = await verifyCredentials(payload);

      if (!error) {
        setCorrelationId(response.headers['x-correlation-id']);
      }

      return { response, error };
    },
    async logout() {
      this.loggedIn = false;
      cancelOidcRefresh();
      stopSmCookieRefresh();
      if (isOidcSession()) {
        const accessToken = localStorage.getItem(ACCESS_TOKEN);
        await logoutRequest(null);
        clearTokens();
        if (accessToken) {
          const appConfigStore = useAppConfigStore();
          submitAimLogoutForm({
            accessToken,
            logoutEndpoint: appConfigStore.aimOidcLogoutEndpoint,
            clientId: appConfigStore.aimOidcClientId,
          });
        }
      } else {
        const refreshTokenValue = localStorage.getItem(REFRESH_TOKEN);
        clearTokens();
        await logoutRequest(refreshTokenValue);
      }
    },
    async refreshToken() {
      const refreshBody = { refresh: localStorage.getItem(REFRESH_TOKEN) || '' };
      try {
        const { data } = await refreshToken(refreshBody);
        setAccessToken(data.access);
        if (data.refresh) {
          setRefreshToken(data.refresh);
        }
        return Promise.resolve(data.access);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async verifyMfaToken(payload: { token: string }) {
      try {
        const { data } = await verifyMfaToken(payload, getCorrelationId());

        removeCorrelationId();

        setAccessToken(data.access_token);
        setRefreshToken(data.refresh_token);

        return { data, error: null };
      } catch (e) {
        const error = e as AxiosError;

        return { data: null, error };
      }
    },
    async resendMfaToken() {
      try {
        const response = await resendMfaToken(null, getCorrelationId());

        setCorrelationId(response.headers['x-correlation-id']);

        return { error: null };
      } catch (e) {
        const error = e as AxiosError;

        return { error };
      }
    },
    async inviteLogin(invitationId: string, payload: InvitationLoginPayload) {
      const { data, error } = await invitationLogin(invitationId, payload);

      if (!error && data.access_token && data.refresh_token) {
        setAccessToken(data.access_token);
        setRefreshToken(data.refresh_token);
        if (payload.locale) {
          setLocaleInLocalStorage(payload.locale);
          loadLocaleMessages(payload.locale);
        }
      }

      return { data, error };
    },
    async deleteMyAccount(password: string) {
      const { error } = await deleteSelf({ email: this.profile.email, password });

      if (!error) {
        await this.logout();
        return { error: null };
      }

      return { error };
    },
  },
});
